import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Plan } from '@app/shared/models/plan';
import { Constant } from 'src/constant';
import { BillingInformations } from '@app/shared/models/billingInformations';
import { Subscription } from '@app/shared/models/subscription';
import { Identity } from '@app/shared/models/identity';
import { computeDiscountPercentage } from '@app/shared/helpers/price';
import { GetPlanGroupOutput } from '@smash-sdk/billing/ejs/01-2024/types/GetPlanGroup/GetPlanGroup';
import { ListCountriesOutput } from '@smash-sdk/vat/ejs/10-2019/types/ListCountries/ListCountries';
import { extractDuration } from 'smash-time';

@Component({
  selector: 'app-smash-signup-form',
  templateUrl: './smash-signup-form.component.html',
  styleUrls: ['./smash-signup-form.component.scss']
})
export class SmashSignupFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() account: Identity;
  @Input() planGroup: GetPlanGroupOutput['group']['plans'];
  @Input() countries: ListCountriesOutput['countries'];
  @Input() computedPlan: Plan;
  @Input() countryCode: string;
  @Input() billingsInformations: BillingInformations;
  @Input() subscription: Subscription;
  @Input() errorSubscription: string;
  @Input() environment: any;
  @Input() planName: string;
  @Input() language: string;
  @Output() formDataUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Output() formDataSubmitted: EventEmitter<any> = new EventEmitter<any>();

  signupForm: FormGroup;
  canLoadForm: boolean;
  selectedPlan: GetPlanGroupOutput['group']['plans'][0];
  filteredPlanGroup: GetPlanGroupOutput['group']['plans'];
  selectedCountry: ListCountriesOutput['countries'][0];
  selectedState: ListCountriesOutput['countries'][0]['states'][0];

  passwordBlur = false;
  hidePassword = true;
  emailBlur = false;
  submitted = false;
  submitting = false;
  vatNumberChanged = false;
  emailChanged = false;
  subscriber;
  CGVaccepted = false;

  constructor(private fb: FormBuilder) { }

  ngOnDestroy() {
    this.subscriber.complete();
    this.subscriber.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.errorSubscription && changes.errorSubscription.currentValue) {
      this.resetChangeDetection();
      this.submitting = false;
    }
  }

  ngOnInit() {
    this.initSignupForm();
    this.subscriber = this.signupForm.valueChanges.subscribe(changes => {
      // Workaround to prevent error ExpressionChangedAfterItHasBeenCheckedError
      const cloneCopy = JSON.parse(JSON.stringify(this.planGroup));
      // Sort & filter on known currencies
      const sortedPlans = cloneCopy.sort((a, b) => b.priceExclTax - a.priceExclTax)
        .filter(plan => plan.currency && Constant.Currencies[plan.currency]);

      setTimeout(() => {
        if (changes.country) {
          if (!changes.country?.eu) {
            this.signupForm.patchValue({ vatNumber: '' }, { emitEvent: false });
          }
          if (changes.country?.states?.length) {
            const stateControl = this.signupForm.get('state');
            stateControl.setValidators(Validators.required);
            stateControl.updateValueAndValidity({ emitEvent: false })
          } else {
            const stateControl = this.signupForm.get('state');
            stateControl.setValidators(null);
            stateControl.updateValueAndValidity({ emitEvent: false })
          }
        }

        if (sortedPlans.length > 1 && changes.country.currency && changes.country.currency !== this.selectedPlan?.currency) {
          this.filteredPlanGroup = sortedPlans.filter(plan => changes.country.currency === plan.currency)
            .map(plan => {
              // PRO & TEAM
              const { quantity, unit } = extractDuration(plan.duration)
              if (quantity !== 1 && unit !== 'months' && !this.accountIsPremium) {
                plan.discountPercentage = computeDiscountPercentage({ plan, countryCurrency: changes.country.currency, plans: this.planGroup })
              }
              return plan;
            });
          this.selectedPlan = sortedPlans.find(plan => plan.currency === changes.country.currency && plan.duration === this.selectedPlan.duration);
        }
        if (changes.payment_type === 'card') {
          this.signupForm.get('card_token').setValidators(Validators.required);
          if (this.signupForm.controls.card_token.errors && this.signupForm.controls.card_token.errors.incorrect) {
            this.signupForm.controls.card_token.setErrors({ incorrect: true });
          } else {
            this.signupForm.controls.card_token.updateValueAndValidity({ emitEvent: false });
          }
        } else if (changes.payment_type === 'paypal') {
          this.signupForm.get('card_token').setValidators(null);
          this.signupForm.patchValue({ card_token: '' }, { emitEvent: false });
          this.signupForm.controls.card_token.setErrors(null);
        }
        this.formDataUpdated.emit(changes);
      }, 10);
    });
  }

  onVatNumberChanges() {
    this.vatNumberChanged = true;
  }

  onEmailChanges() {
    this.emailChanged = true;
  }

  private initSignupForm() {
    if (this.accountIsPremium) {
      if (this.billingsInformations?.countryCode) {
        this.selectedCountry = this.countries.find((country) => country.countryCode === this.billingsInformations.countryCode);
      } else if (this.countryCode) {
        this.selectedCountry = this.countries.find((country) => country.countryCode === this.countryCode);
      } else {
        this.selectedCountry = this.countries.find((country) => country.countryCode === Constant.REGIONS[this.environment.region]); // bind to region country ....
      }

      this.signupForm = this.fb.group({
        plan: ['', [Validators.required]],
        company: [''],
        address: ['', [Validators.required]],
        lastName: ['', [Validators.required]],
        firstName: ['', [Validators.required]],
        city: ['', [Validators.required]],
        zipCode: ['', [Validators.required]],
        country: ['', [Validators.required]],
        vatNumber: [''],
        payment_type: ['card', [Validators.required]],
        card_token: ['', []],
        state: ['', []],
        terms: ['', [Validators.required, Validators.requiredTrue]]
      });
    } else {
      if (this.countryCode) {
        this.selectedCountry = this.countries.find((country) => country.countryCode === this.countryCode);
      } else {
        this.selectedCountry = this.countries.find((country) => country.countryCode === Constant.REGIONS[this.environment.region]); // bind to region country ....
      }

      this.signupForm = this.fb.group({
        plan: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.pattern(Constant.regexpEmail)]],
        password: ['', [Validators.required, Validators.pattern(Constant.passwordPattern)]],
        lastName: ['', [Validators.required]],
        firstName: ['', [Validators.required]],
        company: [''],
        address: ['', [Validators.required]],
        city: ['', [Validators.required]],
        zipCode: ['', [Validators.required]],
        country: ['', [Validators.required]],
        vatNumber: [''],
        payment_type: ['card', [Validators.required]],
        card_token: ['', []],
        state: ['', []],
        terms: ['', [Validators.required, Validators.requiredTrue]]
      });
    }

    const cloneCopy = JSON.parse(JSON.stringify(this.planGroup));
    const sortedPlans = cloneCopy.sort((a, b) => b.priceExclTax - a.priceExclTax).map(plan => {
      // PRO & TEAM
      const { quantity, unit } = extractDuration(plan.duration)
      if (quantity !== 1 && unit !== 'months' && !this.accountIsPremium) {
        plan.discountPercentage = computeDiscountPercentage({ plan, countryCurrency: this.selectedCountry.currency, plans: this.planGroup })
      }
      return plan;
    });
    this.filteredPlanGroup = sortedPlans.filter(plan => this.selectedCountry.currency === plan.currency);

    if (this.billingsInformations && this.billingsInformations.address) {
      this.selectedCountry = this.countries.find((country) => country.countryCode === this.billingsInformations.countryCode);
      this.selectedState = this.selectedCountry?.states?.find(state => state.stateCode === this.billingsInformations.stateCode);
      this.signupForm.patchValue({
        lastName: this.billingsInformations.lastName,
        firstName: this.billingsInformations.firstName,
        company: this.billingsInformations.company,
        address: this.billingsInformations.address,
        city: this.billingsInformations.city,
        zipCode: this.billingsInformations.zipCode,
        vatNumber: this.billingsInformations.vatNumber,
        country: this.selectedCountry,
        state: this.selectedState,
      });
    }
    if (this.subscription && this.subscription.plan) {
      const preselectedPlan = sortedPlans.find((plan: Plan) => plan.id === this.subscription.plan.id);
      const preselectedPlanDuration = sortedPlans.find((plan: Plan) => plan.duration === this.environment.durationChoice && plan.currency === this.selectedCountry.currency);

      if (preselectedPlanDuration) {
        this.selectedPlan = preselectedPlanDuration;
        this.signupForm.patchValue({ plan: preselectedPlanDuration });
      } else if (preselectedPlan) {
        this.selectedPlan = preselectedPlan;
        this.signupForm.patchValue({ plan: preselectedPlan });
        // } else if (preselectedPlanDuration) {
        //   this.selectedPlan = preselectedPlanDuration;
        //   this.signupForm.patchValue({ plan: preselectedPlanDuration });
      } else {
        this.selectedPlan = sortedPlans.find(plan => plan.currency === this.selectedCountry.currency);
        this.signupForm.patchValue({ plan: this.selectedPlan });
      }
    } else {
      this.selectedPlan = sortedPlans.find(plan => plan.currency === this.selectedCountry.currency);
      this.signupForm.patchValue({ plan: this.selectedPlan });
    }
  }

  get f() { return this.signupForm.controls; }

  onPlanChanged() {
    this.signupForm.patchValue({ payment_type: 'card' });
  }

  choosePlanFromSummary(params) {
    const { offerDuration, currency } = params;
    this.selectedPlan = this.planGroup.find(plan => plan.currency === currency && plan.duration === offerDuration);
  }

  stripeDataUpdated({ token, error }) {
    if (error) {
      this.signupForm.controls.card_token.setErrors({ incorrect: true });
      // throw error
    }
    if (token) {
      this.signupForm.patchValue({ card_token: token });
    }
  }

  blurEmail() {
    this.emailBlur = true;
  }

  focusEmail() {
    this.emailBlur = false;
  }

  blurPassword() {
    this.passwordBlur = true;
  }

  focusPassword() {
    this.passwordBlur = false;
  }

  togglePassword() {
    this.hidePassword = !this.hidePassword;
  }

  resetChangeDetection() {
    this.vatNumberChanged = false;
    this.emailChanged = false;
  }

  async onSubmit(form) {
    this.submitted = true;
    this.resetChangeDetection();
    if (this.signupForm.valid && !this.submitting) {
      this.submitting = true;
      this.formDataSubmitted.emit(form);
    }
  }

  get isStripeCardPaymentMethodsEnabled(): boolean {
    return this.selectedPlan?.paymentMethods?.stripeCard?.enabled;
  }

  get isStripeSepaPaymentMethodsEnabled(): boolean {
    return this.selectedPlan?.paymentMethods?.stripeSepa?.enabled;
  }

  get isPaypalPaymentMethodsEnabled(): boolean {
    return this.selectedPlan?.paymentMethods?.paypal?.enabled;
  }

  get accountIsPremium(): boolean {
    return (this.account ? this.account.username !== undefined : false);
  }
}
